import { z } from 'zod';
import { EMAIL_LOCAL_PART_PATTERN } from '../constants/email-sender';
import { GOAL_METRIC_NAMES } from '../constants/goal-metrics';
import { LICENSE_CATEGORY_GROUP_KEYS } from '../constants/license-category';
import { MATCHMAKER_SOURCE_TYPES } from '../constants/license-source-type';
import { RbacActions } from '../constants/rbac-actions';
import { E164_PATTERN } from '../constants/sms-senders';
import { PLATFORM_VOICE_LANGUAGES } from '../constants/platform-engines';
import { OrderStatus } from '../enums/order-status.enum';
import { DEFAULT_WEB_SETTINGS, WebSettingsSchema } from '../forms/web-settings';
import { VISIT_CADENCES } from '../utils/visit-cadence';

/**
 * Schema for Access Logging settings
 */
export const AccessLoggingSchema = z.object({
	verboseLogging: z
		.boolean()
		.default(false)
		.describe('Enable verbose request/response logging'),
});

/**
 * Schema for the System AI Agent (Sales Helper) availability toggle.
 *
 * `enabled` controls whether the code-defined system agent (currently
 * `sales-helper`) is exposed in the CRM chat widget. Opt-in: defaults to
 * `false` so tenants must explicitly turn the widget on. The setting writer
 * also flips `is_chat_widget_default` on the matching `conversation_agent`
 * row so the widget endpoint can read DB state directly.
 */
export const SystemAgentEnabledSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Enable the system AI agent (Sales Helper) for the CRM chat widget. When disabled, the widget is hidden for all users in this tenant.',
		),
});

/**
 * Schema for AI Agent Credits settings
 */
export const ConversationAgentCreditsSchema = z.object({
	voiceMinuteCredits: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Cannot be negative')
		.max(100_000_000, 'Maximum is 100 million minutes')
		.default(100)
		.describe('Monthly voice minute allocation'),

	llmTokenCredits: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Cannot be negative')
		.max(100_000_000_000, 'Maximum is 100 billion tokens')
		.default(1_000_000)
		.describe('Monthly LLM token allocation'),
});

/**
 * Schema for RingCentral integration settings
 */
export const RingCentralSettingsSchema = z.object({
	callMode: z
		.enum(['ringout', 'callout'])
		.default('ringout')
		.describe(
			'Call mode: ringout (calls your phone first) or callout (browser-based)',
		),
});

/**
 * Schema for AI Agent Throttle Concurrency settings
 */
export const ThrottleConcurrencySchema = z.object({
	inbound: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(10)
		.describe('Max concurrent inbound voice and web sessions'),

	outbound: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(10)
		.describe('Max concurrent outbound voice calls'),

	chat: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(10)
		.describe('Max concurrent chat sessions'),
});

/**
 * Schema for the per-verified-domain default outbound sender.
 *
 * Keyed by the domain name exactly as it is registered in the login-DB
 * `_domain` table (lower-cased at read time so casing drift can't hide an
 * entry). Only domains the tenant has actually verified are ever consulted,
 * so an orphaned key is inert rather than dangerous.
 *
 * `localPart` is the part before the `@`; the historical `noreply` is used
 * when a domain has no entry. `fromName` is the optional display name
 * applied when the message itself does not carry one.
 */
export const EmailDomainSendersSchema = z.object({
	domains: z
		.record(
			z.string(),
			z.object({
				localPart: z
					.string()
					.regex(
						EMAIL_LOCAL_PART_PATTERN,
						'Local part may contain only letters, numbers, dot, underscore, plus and hyphen (max 64)',
					)
					.describe('Part before the @ — e.g. "sales" for sales@acme.com'),
				fromName: z
					.string()
					.max(150)
					.optional()
					.describe('Optional display name shown to recipients'),
			}),
		)
		.default({})
		.describe('Per-domain default sender, keyed by verified domain name'),
});

/**
 * Schema for the tenant-level lead registration notification recipient.
 * String form (single email) is the simple case; comma-separated list
 * works for multiple recipients without needing array gymnastics in the
 * admin UI. Empty string treated by callers as "not configured" so
 * unset tenants fall through to the system default / log warning path.
 */
export const LeadRegistrationEmailSchema = z
	.string()
	.default('')
	.describe(
		'Email recipient(s) for new lead registrations. Single address or comma-separated list. Empty = not configured.',
	);

/**
 * Schema for the tenant-level ERP integration mode.
 *
 * Two modes:
 *  - `workd_as_erp` (default) — Workd IS the ERP for this tenant. Orders
 *    that land in APPROVED status stay inside Workd, so the user can
 *    cancel them (and the trip-cancel flow can release / cancel them
 *    with auth-void).
 *  - `third_party` — Workd integrates with a downstream ERP (NetSuite,
 *    SAP, Acumatica, QuickBooks-Enterprise, etc.). Once an order is
 *    APPROVED it gets pushed out and we no longer control its
 *    lifecycle, so cancel-after-approval is rejected. (Note: as of
 *    2026-05-22 no actual third-party-ERP sync code exists in this
 *    codebase — the restriction has been theoretical. Tenants who add
 *    an ERP integration later flip this setting and wire their sync.)
 *
 * Cancel-order gate uses this setting at apps/crm-api/src/modules/
 * orders/orders.service.ts:cancelOrder — when `mode === 'workd_as_erp'`
 * the cancellable-status list includes APPROVED.
 */
export const ErpIntegrationSchema = z.object({
	mode: z
		.enum(['workd_as_erp', 'third_party'])
		.default('workd_as_erp')
		.describe(
			'workd_as_erp: orders remain inside Workd and stay cancellable after approval. third_party: orders sync to an external ERP and become non-cancellable post-approval.',
		),
});

/**
 * Schema for Pharmacy License Compliance settings.
 *
 * `enabled` controls whether the tenant uses pharmacy compliance features.
 *
 * Match confirmation defaults to human-only: every registry match lands as
 * `pending_review` for explicit confirmation on the License Matchmaker screen.
 * PD-14391 makes that opt-out-able per tenant through the `autoConfirm*` keys
 * below (all default to the human-only behavior). Old `autoApproveThreshold`
 * and `notifyOnAutoPair` keys may still appear in legacy instance_setting rows
 * but are no longer read.
 */
export const PharmacyComplianceSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Master switch for the licensing system. When OFF (default), every licensing background job (registry harvest, matchmaker scans, registry-status detection, mark-expired sweep, daily license notifications, compliance-stats refresh) is skipped for this tenant and order-validation skips license-status enforcement. Turn ON only after the tenant is set up to use pharmacy compliance (NPI, DEA verification, state board registry sync, enhanced order blocking).',
		),
	requireVerifiedLicenseAtCheckout: z
		.boolean()
		.default(false)
		.describe(
			'Customer-facing checkout enforcement (PD-14368). When OFF (default), the order-checkout license-good-standing surfaces are suppressed: the cart sidebar shows no license indicator and order submission is never blocked for a client lacking a confirmed/verified license — even while the rest of the licensing subsystem (matchmaker, dashboard, registry harvest) keeps running. Turn ON per-tenant only once matchmaking has been underway and licensing is being managed the new way, so we do not nag customers with negative-sentiment checkout warnings during ramp-up. Has effect only when `enabled` is also true.',
		),
	licenseCategories: z
		.array(z.string())
		.default(['PHARMACY'])
		.describe(
			'License categories to monitor (e.g. PHARMACY, PHARMACIST, TECHNICIAN)',
		),
	// PD-14842: per-tenant matchmaking toggles. Stored as DISABLED sets (default
	// empty = everything matched) so a newly-added source/category stays enabled
	// for existing tenants without a backfill. The matchmaker reads these to skip
	// source-specific passes and to filter the state registry slice.
	disabledMatchSourceTypes: z
		.array(z.enum(MATCHMAKER_SOURCE_TYPES))
		.default([])
		.meta({
			title: 'Disabled matchmaking sources',
			description:
				'Registry source types EXCLUDED from matchmaking for this tenant. Checked = excluded. Empty (default) = every source is matched. Example: check NPI to stop the matchmaker suggesting NPI records.',
		}),
	disabledMatchCategoryGroups: z
		.array(z.enum(LICENSE_CATEGORY_GROUP_KEYS))
		.default([])
		.meta({
			title: 'Disabled matchmaking categories',
			description:
				'License-category groups EXCLUDED from matchmaking for this tenant. Checked = excluded. Empty (default) = every category is matched. Example: check Veterinary to stop veterinary state-board matches while pharmacy state-board matching continues.',
		}),
	// PD-14391: per-tenant AUTO-CONFIRM policy. Distinct from the
	// `disabledMatch*` sets above — those decide what the matchmaker even
	// SUGGESTS; these decide which of those suggestions may be confirmed
	// without a human click. Every value here is ADDITIVE on top of the
	// unconditional Tier-1 safety floor enforced in
	// `LicenseMatchReviewService.autoConfirmHighConfidence` (exact raw license
	// number + same state + identical name + single candidate + Active
	// registry record), so no combination of these settings can make
	// auto-confirm LOOSER than that floor — only stricter.
	//
	// Polarity note: the two scope fields are DISABLED sets (deny-lists), the
	// same convention as `disabledMatchSourceTypes` /
	// `disabledMatchCategoryGroups` directly above, so the four checkbox lists
	// in the settings dialog all read "checked = excluded" rather than flipping
	// meaning halfway down the form. Default OFF is carried by
	// `autoConfirmEnabled: false` (nothing auto-confirms at all) plus
	// `autoConfirmMinConfidence: 100` (the strictest score the matcher can
	// produce), NOT by the scope sets — an allow-list would additionally be
	// inexpressible for categories, because `LICENSE_CATEGORY_GROUP_KEYS`
	// covers only VETERINARY and a pharmacy state-board match belongs to no
	// group at all.
	autoConfirmEnabled: z.boolean().default(false).meta({
		title: 'Auto-confirm matches',
		description:
			'When ON, the matchmaker may confirm a pending match without a human click. DEFAULT OFF: every match lands in Pending Confirmation for a reviewer. Even when ON, only matches that clear the confidence threshold, the source/category scope below, AND the built-in Tier-1 safety rule (exact license number, same state, identical name, exactly one candidate, Active registry record) are ever auto-confirmed.',
	}),
	autoConfirmMinConfidence: z.number().min(50).max(100).default(100).meta({
		title: 'Auto-confirm confidence threshold',
		description:
			'Minimum match confidence (0-100) required to auto-confirm. 100 (default) means only a perfect score qualifies — an exact license-number match whose client and registry names are identical. Lowering this can never admit a match that fails the Tier-1 safety rule; it only narrows further. Ignored while Auto-confirm matches is OFF.',
	}),
	autoConfirmDisabledSourceTypes: z
		.array(z.enum(MATCHMAKER_SOURCE_TYPES))
		.default([])
		.meta({
			title: 'Auto-confirm: excluded sources',
			description:
				'Registry source types that may NEVER auto-confirm for this tenant, even at full confidence. Checked = excluded. Empty (default) = every source that clears the other gates may auto-confirm. Matches excluded here still appear as suggestions for human review.',
		}),
	autoConfirmDisabledCategoryGroups: z
		.array(z.enum(LICENSE_CATEGORY_GROUP_KEYS))
		.default([])
		.meta({
			title: 'Auto-confirm: excluded categories',
			description:
				'License-category groups that may NEVER auto-confirm for this tenant, even at full confidence. Checked = excluded. Empty (default) = every category that clears the other gates may auto-confirm. Matches excluded here still appear as suggestions for human review.',
		}),
	notifyOnStatusChange: z
		.boolean()
		.default(false)
		.describe(
			'Send notifications when a license status changes (e.g. Active → Suspended). Defaults off — must be opted in per tenant.',
		),
	// PD-16306: notifyOnExpiring, notifyDaysBeforeExpiry and
	// notifyOnDisciplinaryAction were the daily digest's switches and went with
	// it. The weekly compliance summary that replaced the digest is switched by
	// its recipient list (Settings > Notifications > Recipients), and a stored
	// value still carrying the old keys parses without them.
});

/**
 * Schema for the order-day license check gate (PD-14806/PD-14807).
 *
 * A separate opt-in from the master `licensing.pharmacy_compliance` switch: a
 * license can be revoked/suspended/disciplined any day, so this re-verifies every
 * license stamped on an order AS OF SHIP DAY before the order reaches the ERP.
 * DEFAULT OFF because most licenses are not yet human-confirmed/matched, so gating
 * today would hold nearly every order — it turns on per tenant once confirmations
 * accumulate. The flag flip and the gate code must deploy together.
 */
export const OrderLicenseCheckSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'When ON, an order entering checkout is held in "Pending License Check" until every license stamped on it is verified good as of today (then it takes the tenant’s normal submission status and flows to the ERP), or a check fails/times out (then it is held for Compliance Review and the compliance team is emailed). DEFAULT OFF: enable per tenant only once matched licenses have accumulated, or nearly every order would be held. Has effect only when `licensing.pharmacy_compliance.enabled` is also true.',
		),
	timeoutMinutes: z
		.number()
		.min(1)
		.max(60)
		.default(10)
		.describe(
			'How long an order may sit in "Pending License Check" before the timeout watchdog routes it to Compliance Review with the last-known status (guarantees the 10–15 minute expectation and makes a verification-machine outage degrade safely to human review, never a silent hang).',
		),
});

/**
 * Schema for the tenant-level "Pharmacy Sales" feature flag.
 *
 * Distinct from `PharmacyComplianceSchema` — that one is a per-tenant
 * licensing-subsystem switch (registry harvest, matchmaker, order
 * blocking). `PharmacySalesSchema.enabled` is the higher-level question
 * "does this tenant sell to pharmacy clients?" — when true the SPA shows
 * pharmacy-specific UI (NPI / NCPDP fields on client, pharmacy labeling,
 * future pharmacy-only features). Replaces the legacy `med-mover-feature`
 * `_preference` row in v2 instances.
 */
export const PharmacySalesSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'When ON, the tenant sells to pharmacy clients and the CRM exposes pharmacy-specific UI (NPI / NCPDP fields on the client editor, pharmacy labeling, etc.). Independent of licensing.pharmacy_compliance — a tenant can sell to pharmacies without subscribing to the license-monitoring subsystem.',
		),
});

/**
 * PD-15423 — cross-account delivery. One client sometimes orders and pays
 * for product delivered to ANOTHER client's site (e.g. a caterer running an
 * event at another client's venue) with no parent/child relationship between
 * them. When enabled, CRM order entry lets a salesperson pick another client
 * (and one of that client's addresses) as the order's delivery destination;
 * the order and invoice stay on the ordering client.
 */
export const CrossAccountDeliverySchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			"When ON, CRM order entry offers 'Deliver to another customer': the order ships to a different client's address while the order and invoice remain on the ordering client. Off by default — enable only for tenants whose clients order for delivery to each other's sites.",
		),
});

export const UrlSettingsSchema = z.object({
	crm: z.string().describe('The URL of the CRM application'),
});

/**
 * Shared schema for PDF document settings (order and quote)
 *
 * `title` metadata is the human label the config-web SettingFormDialog
 * renders for each field (falling back to the raw field name when
 * absent). `.meta({ title, description })` replaces `.describe(...)` —
 * describe is just sugar for `.meta({ description })` in Zod 4, so the
 * description behavior is unchanged.
 */
export const PdfSettingsSchema = z.object({
	senderName: z.string().nullable().optional().meta({
		title: 'Sender Name',
		description:
			'Override sender name (default: theme company name). Set null to omit.',
	}),
	senderEmail: z.string().nullable().optional().meta({
		title: 'Sender Email',
		description:
			'Override sender email (default: theme company email). Set null to omit.',
	}),
	senderAddressLines: z.array(z.string()).optional().meta({
		title: 'Sender Address Lines',
		description: 'Address lines shown under sender name',
	}),
	// PD-15298 — the seller's phone. Added here rather than as a new setting so
	// the sender identity (name / email / address / phone) stays in one place
	// and the printed delivery receipt cannot drift from the invoice PDF, which
	// already reads its sender block from this schema. Omitted entirely — label
	// and value — when unset, so no empty row appears on a template.
	senderPhone: z.string().nullable().optional().meta({
		title: 'Sender Phone',
		description:
			'Phone shown under the sender address. Leave blank to omit it entirely.',
	}),

	headerMessage: z.string().optional().meta({
		title: 'Header Message',
		description: 'HTML message above line items',
	}),
	footerMessage: z.string().optional().meta({
		title: 'Footer Message',
		description: 'HTML message below the summary',
	}),

	showSender: z.boolean().default(true).meta({
		title: 'Show Sender',
		description: 'Show the Sender section',
	}),
	showDiscount: z.boolean().default(true).meta({
		title: 'Show Discount',
		description: 'Show the Discount row',
	}),
	showShipping: z.boolean().default(true).meta({
		title: 'Show Shipping',
		description: 'Show the Shipping row',
	}),
});

/**
 * PD-15298 — settings for the printed delivery receipt.
 *
 * Separate from the PDF schemas on purpose: a thermal receipt is a different
 * document with different constraints (42 columns, no HTML, no images beyond
 * the logo), so folding its copy into `pdf.invoice` would invite HTML being
 * pasted into a field that can only ever print plain text.
 */
export const ReceiptSettingsSchema = z.object({
	thankYouMessage: z.string().default('Thank you for your business!').meta({
		title: 'Thank-you Message',
		description:
			'Printed, centred, at the bottom of every delivery receipt. Plain text only — the receipt is 42 characters wide, so anything longer wraps. Leave blank to print nothing.',
	}),
});

/**
 * Invoice PDF extends with invoice-specific toggles
 */
export const InvoicePdfSettingsSchema = PdfSettingsSchema.extend({
	showDetailsBox: z.boolean().default(true).meta({
		title: 'Show Details Box',
		description: 'Show the Details box (PO#, terms, due date, credit)',
	}),
	// PD-14983 — the remittance block always states the amount due and who to
	// pay; this is the tenant's chance to add the specifics (remit-to address,
	// ACH/wire details, a portal link). Empty by default so nothing invented
	// appears on an invoice until a tenant fills it in.
	remittanceInstructions: z.string().default('').meta({
		title: 'Remittance Instructions',
		description:
			'Payment instructions printed in the Remittance block on invoices — for example a remit-to address, ACH details, or a link to pay online.',
	}),
});

/**
 * Schema for the "Powered by X" attribution footer rendered across CRM
 * web, mobile (Capacitor), and email PDFs.
 *
 * 2026-05-23 — added so tenants can decide whether and how to attribute
 * the underlying platform. Default-on per Workd policy: every tenant
 * shows "Powered by Workd" unless they explicitly opt out, so the
 * platform gets visible credit across all the customer-facing surfaces.
 *
 * Per-storefront override lives in `PortalSiteSettings.brandAttribution`
 * (deferred to a follow-on pass — see `docs/claude/motor-city-defects/
 * branding-plan.md` for the storefront-override design).
 */
/**
 * Schema for the "Sanitize Workd users" tenant setting.
 *
 * When `enabled` is true (default for customer tenants), backend
 * user-LIST surfaces (Drivers list, Lead Eligibility, Activity Logs,
 * Login History) filter out Workd / Developrise staff for non-Workd
 * callers. The intent is to prevent a tenant admin from accidentally
 * picking a Workd staffer for production work when we happen to be
 * inside their tenant for testing/config.
 *
 * When `enabled` is false (set on internal tenants like `workd-sales`
 * where Workd staff ARE the real users), no sanitization happens —
 * Workd users are visible everywhere because they're the actual
 * operational identities.
 *
 * Default `true` is the conservative choice: a new customer tenant
 * gets sanitized automatically; an internal tenant has to opt out
 * explicitly. Only applies to user-LIST surfaces — assigned-entity
 * surfaces (route default driver, trip driver, client salesperson,
 * creator / modifier audit) always show the truth regardless of this
 * setting. See `libs/shared-server/src/utils/workd-user-filter.ts`
 * for the helper and CLAUDE.md "Workd User Visibility" for policy.
 */
export const SanitizeWorkdUsersSchema = z.object({
	enabled: z
		.boolean()
		.default(true)
		.describe(
			'When ON (default), filter Workd/Developrise staff out of user-list surfaces (Drivers list, Lead Eligibility, Activity Logs, Login History) for non-Workd callers. Turn OFF for internal tenants (workd-sales etc.) where Workd staff are the real operational users.',
		),
});

/**
 * PD-16765: the tenant admin's on/off switch for the Claude connector (the
 * public MCP endpoint). Off by default; a missing row also reads as off.
 */
export const McpEnabledSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe('Allow people in this company to connect Claude to the CRM.'),
});

/**
 * PD-16765: Workd-only switch that lets the MCP endpoint accept CRM sign-in
 * and Developer-key tokens. Until connector sign-in (OAuth) exists these are
 * the only tokens a client can send, so the endpoint needs this on to work at
 * all. Off by default; meant for staging and internal testing only.
 */
export const McpAllowDeveloperKeysSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Accept CRM sign-in and Developer-key tokens on the Claude connector.',
		),
});

/**
 * PD-16766: the Claude connector's rate limits. These limits are placeholders
 * (Q-37) and Workd sets them per tenant. Every field is optional: a field
 * that is absent, or a tenant with no row, uses DEFAULT_AI_RATE_LIMITS.
 */
export const DEFAULT_AI_RATE_LIMITS = {
	mcpUserPerMinute: 60,
	mcpUserPer10Seconds: 20,
	mcpTenantPerMinute: 600,
	mcpHeavyConcurrency: 5,
} as const;

export const AiRateLimitsSchema = z.object({
	mcpUserPerMinute: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100_000, 'Maximum is 100,000')
		.optional()
		.describe('Claude connector tool calls one person may make per minute'),
	mcpUserPer10Seconds: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100_000, 'Maximum is 100,000')
		.optional()
		.describe('Claude connector tool calls one person may make in 10 seconds'),
	mcpTenantPerMinute: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(1_000_000, 'Maximum is 1,000,000')
		.optional()
		.describe('Claude connector tool calls the whole company may make per minute'),
	mcpHeavyConcurrency: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(1_000, 'Maximum is 1,000')
		.optional()
		.describe('Claude connector reports the company may run at the same time'),
});

/**
 * Ecommerce user role defaults (PD-13481).
 *
 * The ecommerce user create/edit panel offers two roles — `Ecommerce Customer`
 * (labelled "Buyer") and `Ecommerce Admin`. That choice was built for a tenant
 * that wanted it; others want every portal user to land on one role without
 * their staff deciding per user.
 *
 * `defaultRoleName` pre-selects a role in the create flow; `allowRoleChange`
 * decides whether the operator may change it. The pair is deliberately
 * independent: a default with the change still allowed is a convenience,
 * a default with the change locked removes the decision entirely.
 *
 * Registry default reproduces today's behaviour — no default, freely
 * changeable. Locking without a default is meaningless (there would be
 * nothing to fall back to), so the panel keeps the role selectable in that
 * combination.
 *
 * The role names are enumerated rather than free text because the panel only
 * ever offers these two roles; a typo in a free-text field would silently
 * resolve to "no default".
 */
export const EcommerceUserRoleDefaultsSchema = z.object({
	defaultRoleName: z
		.enum(['Ecommerce Customer', 'Ecommerce Admin'])
		.nullable()
		.default(null)
		.describe(
			'Role pre-selected for a new ecommerce user. Null (default) = no pre-selection, the operator picks.',
		),
	allowRoleChange: z
		.boolean()
		.default(true)
		.describe(
			'When ON (default), the operator may change the role on the ecommerce user panel. Turn OFF (with a default role set) to show the role read-only and always save the default.',
		),
});

/** Bounds for the idle sign-out minutes (PD-16414): one minute to one day. */
export const IDLE_TIMEOUT_MIN_MINUTES = 1;
export const IDLE_TIMEOUT_MAX_MINUTES = 1440;

const IdleTimeoutMinutesSchema = z
	.number()
	.int('Must be a whole number of minutes')
	.min(
		IDLE_TIMEOUT_MIN_MINUTES,
		`Minimum is ${IDLE_TIMEOUT_MIN_MINUTES} minute`,
	)
	.max(
		IDLE_TIMEOUT_MAX_MINUTES,
		`Maximum is ${IDLE_TIMEOUT_MAX_MINUTES} minutes (one day)`,
	)
	.nullable()
	.default(null);

/**
 * Idle sign-out (PD-16414).
 *
 * How long a signed-in user may be inactive before they are signed out: one
 * value for the CRM and one for the ecommerce storefront, because the rules
 * differ. PCI DSS 8.2.8 asks for re-authentication after 15 idle minutes on
 * systems that handle card data (the CRM takes payments), while a storefront
 * shopper is commonly given longer.
 *
 * Enforcement is client-side: each app watches for real user input
 * (pointer, keyboard, touch, wheel), shares the last-activity time across
 * tabs, warns a minute before the limit and signs out when it passes,
 * revoking the session on the server. Null (the default for every tenant)
 * turns the feature off for that app.
 */
export const IdleTimeoutSchema = z.object({
	crmMinutes: IdleTimeoutMinutesSchema.describe(
		'Minutes of inactivity after which a CRM user is signed out. Empty (default) = never.',
	),
	storefrontMinutes: IdleTimeoutMinutesSchema.describe(
		'Minutes of inactivity after which an ecommerce storefront shopper is signed out. Empty (default) = never.',
	),
});

/**
 * Governor for record imports (PD-12353). Workd pays the geocoding bill for
 * imported addresses (tenants do not), so the cap is a Workd-held dial:
 * lower it for a tenant that overuses imports rather than warning uploaders
 * who have no incentive to care. Unseeded tenants fall back to the code
 * default in the import service.
 */
export const ImportMaxRowsSchema = z.object({
	maxRows: z
		.number()
		.int()
		.positive()
		.default(10000)
		.describe(
			'Maximum data rows a single record-import run may contain. Files with more data rows are rejected at upload.',
		),
});

/**
 * Schema for the account-funds DEPOSIT switch (PD-14922).
 *
 * Splits customer account funds into the two halves that were
 * previously all-or-nothing behind the 'Account Funds' supported
 * payment method type:
 *
 *   - SPENDING existing funds — available whenever the method type is
 *     configured. NOT governed by this setting.
 *   - DEPOSITING new money (the `onAccountAmount` portion of a payment)
 *     — governed by this setting.
 *
 * The split exists because an ERP can be unable to represent a deposit
 * while handling spending fine. HF Foods' Workday attaches a card
 * processing fee to an invoice as an "invoice adjustment"; a payment
 * placed on account has no invoice to attach it to, so the fee has
 * nowhere to land and the transaction cannot be applied. Turning the
 * payment method type off entirely would also stop customers spending
 * the balance they already hold, which has to keep working.
 *
 * Defaults to true so every existing tenant keeps today's behavior; a
 * tenant whose ERP cannot record a deposit opts out explicitly.
 */
/**
 * Default visit cadence by account kind (PD-16083). Applies to any client
 * whose own cadence is unset; coverage (due / overdue rings on the maps and
 * need ordering in the planners) is measured against the effective value.
 */
export const GoogleGeocodingFallbackSchema = z.object({
	enabled: z.boolean().default(true),
});

const DefaultVisitCadenceSchema = z.object({
	customer: z
		.enum(VISIT_CADENCES)
		.default('monthly')
		.describe('Cadence for customers with no cadence of their own.'),
	lead: z
		.enum(VISIT_CADENCES)
		.default('none')
		.describe('Cadence for leads with no cadence of their own.'),
});

export const AllowAccountFundsDepositSchema = z.object({
	enabled: z
		.boolean()
		.default(true)
		.describe(
			'When ON (default), customers may add money to their account funds balance (the "Apply to Customer Account" amount on Make Payment). Turn OFF for tenants whose ERP cannot record an on-account deposit. Spending existing account funds is unaffected either way.',
		),
});

/**
 * Schema for paying invoices with credit memo balances (PD-15441).
 *
 * When enabled, open credit memos (RMAs) appear alongside open invoices on
 * the Make a Payment screens (CRM and customer portal) as credit rows. The
 * customer applies some or all of a memo's remaining balance against the
 * invoices they are paying, and only the net difference is charged to their
 * payment method. The applications are recorded per document — +amount to
 * each invoice, -amount to each credit memo — which is exactly the shape the
 * ERP payment export sends (HF Foods' Workday applies the payment to the
 * invoice and the RMA in one remittance advice).
 *
 * Defaults to false: PD-15014 deliberately removed credit memos from the
 * payable list because the ERP sync stores their outstanding balance with
 * the opposite sign and they rendered as charge-able rows. Only tenants
 * whose ERP expects credit-memo applications on payments should opt in.
 */
export const PayInvoicesWithCreditMemosSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'When ON, open credit memos (RMAs) can be applied against invoices on the Make a Payment screens, charging only the net difference. Requires an ERP integration that accepts credit-memo applications on exported payments (e.g. HF Foods Workday). OFF by default.',
		),
});

/**
 * Schema for requiring an account-funds spend to use the whole balance
 * (PD-15585).
 *
 * A customer's account-funds balance is, in an ERP like HF Foods' Workday,
 * a single customer payment sitting unapplied. Workday only accepts an
 * application that consumes the whole payment: spend part of it and the
 * record is left in Draft, untouched by Auto-Apply. So when this is ON,
 * choosing Account Funds for less than the full available balance is
 * refused on every payment surface (Make Payment in the CRM and the portal,
 * the invoice payment screens, checkout), and the automatic funds-first
 * draw the portal and checkout perform on a card payment is skipped when it
 * would leave part of the balance behind. A spend of exactly the whole
 * balance is unaffected, including one that only part-pays an invoice or is
 * paired with a card for the rest.
 *
 * The message shown is configurable per surface because the reader
 * differs: a salesperson in the CRM versus the customer on the portal.
 * `{balance}` in either message is replaced with the formatted available
 * balance.
 *
 * Defaults to OFF: every tenant keeps today's behavior unless its ERP has
 * this constraint.
 */
export const RequireFullAccountFundsSpendSchema = z.object({
	enabled: z.boolean().default(false).meta({
		title: 'Enabled',
		description:
			'When ON, Account Funds can only be spent in full: a payment that would leave part of the balance unused is blocked, and the automatic funds-first draw on card payments is skipped when it would be partial. Use for tenants whose ERP cannot apply part of an on-account payment (HF Foods Workday). OFF by default.',
	}),
	crmMessage: z
		.string()
		.default(
			'Account funds have to be used in full in one payment. This customer’s balance is {balance}. Apply all of it, or wait until there are enough open invoices to use the whole amount.',
		)
		.meta({
			title: 'CRM Message',
			description:
				'Shown to staff on the CRM payment screens when a partial Account Funds spend is blocked. {balance} is replaced with the available balance.',
		}),
	portalMessage: z
		.string()
		.default(
			'Your account funds have to be used in full in one payment. Your balance is {balance}. Select enough invoices to use all of it, or wait until you have another invoice to pay.',
		)
		.meta({
			title: 'Portal Message',
			description:
				'Shown to customers on the ecommerce portal when a partial Account Funds spend is blocked. {balance} is replaced with the available balance.',
		}),
});

/**
 * Schema for blocking a payment whose total is $0 (PD-15585).
 *
 * With `payments.pay_invoices_with_credit_memos` on, a customer can apply
 * credit memos that cover the selected invoices entirely, leaving nothing to
 * charge. Workd records that as a $0 "record the applications" payment, but
 * some ERPs cannot take it: Workday's Put_Customer_Payment refuses a zero
 * Payment_Amount outright, and there is no operation that applies an
 * existing credit memo to an invoice without a payment. When this is ON the
 * $0 payment is refused on every surface that offers credit memos, with a
 * message telling the user to leave an amount to pay or have the credit
 * applied on the ERP side.
 *
 * Per-surface messages, as for the account-funds setting above.
 *
 * Defaults to OFF: PD-15441 made the $0 payment a supported shape and other
 * tenants keep it.
 */
export const BlockZeroTotalPaymentsSchema = z.object({
	enabled: z.boolean().default(false).meta({
		title: 'Enabled',
		description:
			'When ON, a payment whose total is $0 because credit memos cover the selected invoices is blocked on the CRM and portal payment screens. Use for tenants whose ERP cannot record a zero-amount payment (HF Foods Workday). OFF by default.',
	}),
	crmMessage: z
		.string()
		.default(
			'Credit memos cannot cover the whole payment. Leave an amount to pay, or have accounting apply the credit to the invoice directly.',
		)
		.meta({
			title: 'CRM Message',
			description:
				'Shown to staff on the CRM payment screens when a $0 credit-covered payment is blocked.',
		}),
	portalMessage: z
		.string()
		.default(
			'Credit memos cannot cover the whole payment. Leave an amount to pay, or contact your account representative to have the credit applied for you.',
		)
		.meta({
			title: 'Portal Message',
			description:
				'Shown to customers on the ecommerce portal when a $0 credit-covered payment is blocked.',
		}),
});

/**
 * PD-16594 — the tenant number each kind of automated text is sent from. The CRM screen
 * (Settings → Communications → Text Messages) only offers the tenant's own text-capable
 * numbers on its Twilio sub-account and checks that again when it saves.
 */
const SmsSenderNumberSchema = z
	.string()
	.regex(
		E164_PATTERN,
		'Use the number in international form, e.g. +12485551234',
	)
	.nullable();

export const SmsSendersSchema = z.object({
	sign_in: SmsSenderNumberSchema,
	welcome: SmsSenderNumberSchema,
	delivery: SmsSenderNumberSchema,
	// PD-16475 — added after tenants had already saved this setting, so a saved
	// value without it must still parse (as "Workd's shared number").
	notification: SmsSenderNumberSchema.default(null),
});

/**
 * PD-16725 — a tenant's own extra phone numbers that may receive its automated
 * texts outside production.
 *
 * A non-production crm-api (staging, local) keeps automated texts (sign-in
 * codes, welcome and password-reset texts, storefront notifications, delivery
 * notifications) to an allow-list so test runs cannot text real customers; a
 * number that is not on it gets a simulated send that is only logged. The
 * deploy's own list (`SMS_WHITELIST`) holds Workd staff numbers and applies to
 * every tenant. This setting adds the tenant's testers during UAT without
 * editing the shared task definition, and only for that tenant.
 *
 * Edited in the CRM under Settings → Communications → Text Messages, which
 * stores every number in E.164. No row means an empty list. Has no effect in
 * production, which has no allow-list.
 */
export const SmsStagingAllowListSchema = z.object({
	numbers: z
		.array(
			z
				.string()
				.regex(
					E164_PATTERN,
					'Use the number in international form, e.g. +12485551234',
				),
		)
		.default([])
		.describe(
			'Extra phone numbers (E.164) that receive this tenant’s automated texts outside production.',
		),
});

/**
 * Schema for the tenant's Twilio subaccount binding (PD-14218).
 *
 * `subAccountSid` is the Twilio subaccount (`AC…`) that owns this
 * tenant's softphone numbers. Hydrated onto TenantContext by the
 * unified-auth flow and read via `ContextService.getTwilioSubAccountSid()`
 * so authenticated rep call-control ops (coaching, recording control) and
 * phone-context-less paths (E911 address registration) re-root their Twilio
 * REST resource navigation to the subaccount instead of the master account.
 * Server-only (no `isPublic`). Empty string = tenant not yet migrated to a
 * subaccount → falls back to the Twilio master account (null-safe,
 * behavior unchanged pre-cutover).
 */
export const TwilioSubAccountSidSchema = z.object({
	subAccountSid: z
		.string()
		.default('')
		.describe(
			'Twilio subaccount SID (AC…) that owns this tenant’s softphone numbers. Set during the subaccount migration. Empty = not migrated; the runtime falls back to the Twilio master account.',
		),
});

/**
 * Schema for the master Twilio softphone on/off toggle (PD-14243).
 *
 * The explicit kill-switch the Workd team flips to enable/disable the human
 * Twilio softphone for a tenant WITHOUT deleting credentials. Only ONE input
 * to the derived gate (`getTwilioFeatureEnabled`): the softphone is truly "on"
 * only when this is true AND the tenant has a provisioned subaccount SID +
 * voice key (SSM) + ≥1 Kari's Law recipient. Written by the config-app
 * Enable-Phone flow (turns it on as part of provisioning) and the crm-api
 * kill-switch endpoint (flips it off in an emergency) — both write this same
 * key, so there is no drift. Server-only.
 */
export const TwilioFeatureEnabledSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Master on/off for the tenant human softphone. Off by default; only one input to the derived "on" gate (which also needs a subaccount + voice key + Kari\'s Law recipients).',
		),
});

export const BrandAttributionSchema = z.object({
	show: z
		.boolean()
		.default(true)
		.describe(
			'Render the "Powered by X" footer in the app shell. When off, the footer is hidden everywhere (CRM web, mobile, email PDFs).',
		),
	label: z
		.string()
		.max(60, 'Label must be 60 characters or fewer')
		.nullable()
		.default(null)
		.describe(
			'The brand name shown after the fixed "Powered by " prefix (e.g. "Workd", "Ice Mover"). Leave blank for the brand default. A leading "Powered by " is stripped on render, so legacy values like "Powered by Acme" still display correctly.',
		),
	logoUrl: z
		.string()
		.url('Must be a valid URL')
		.nullable()
		.default(null)
		.describe(
			'Optional logo image URL shown in place of the brand name after "Powered by " (e.g. the Workd wordmark). When set, the logo replaces the text label. Leave blank to show the text label instead.',
		),
	logoUrlDark: z
		.string()
		.url('Must be a valid URL')
		.nullable()
		.default(null)
		.describe(
			'Optional dark-mode logo image URL, used in place of logoUrl when the app is in dark mode (e.g. a white wordmark). Falls back to logoUrl when not set.',
		),
	linkUrl: z
		.string()
		.url('Must be a valid URL')
		.nullable()
		.default('https://workd.com')
		.describe(
			'Optional click-through URL for the attribution. Leave blank to render plain text with no link.',
		),
});

/**
 * Registry mapping setting keys to their schemas and metadata
 *
 * Key format: Technical identifier stored in database
 *   - ACCESS_LOGGING (upper snake case, like env var)
 *   - conversation_agents.monthly_credits (dot notation for namespacing)
 *
 * Label format: Human-readable display name (in registry, NOT in database)
 *   - "Access Logging" - shown in UI
 *   - "AI Agent Credits" - shown in UI
 */
/**
 * Statuses a tenant may pick as the post-submission default. Deliberately a
 * SUBSET of OrderStatus — excludes transient/system statuses (unsubmitted,
 * quote, cancelled, override*) that make no sense as a submission landing
 * state. PD-14226.
 */
export const DEFAULT_SUBMISSION_STATUS_OPTIONS: ReadonlyArray<OrderStatus> = [
	OrderStatus.PENDING,
	OrderStatus.APPROVED,
	OrderStatus.APPROVAL_REQUIRED,
	OrderStatus.SHIPPED,
];

/**
 * Schema for the per-tenant default order-submission status, configurable
 * separately for CRM and ecommerce. This is the status an order lands in on
 * submission UNLESS the order-submission checks force Approval Required
 * (those always win — see orders.service.submitOrder /
 * portal-cart-submission.service.submitCart). Setting ecommerce to Approval
 * Required is how a tenant auto-holds every storefront order before it
 * reaches the ERP.
 */
export const DefaultSubmissionStatusSchema = z.object({
	crm: z
		.nativeEnum(OrderStatus)
		.refine((s) => DEFAULT_SUBMISSION_STATUS_OPTIONS.includes(s), {
			message: 'Not an allowed default submission status',
		})
		.default(OrderStatus.PENDING),
	ecommerce: z
		.nativeEnum(OrderStatus)
		.refine((s) => DEFAULT_SUBMISSION_STATUS_OPTIONS.includes(s), {
			message: 'Not an allowed default submission status',
		})
		.default(OrderStatus.PENDING),
	// PD-14234: roles (rbac_role ids) additionally allowed to approve/reject
	// orders held ONLY because the channel default status is Approval
	// Required (the DEFAULT_SETTING approval reason). Users holding the
	// blanket orders:approve / orders:reject actions are always allowed and
	// don't need to be listed here.
	crmApproverRoleIds: z.array(z.number()).optional(),
	ecommerceApproverRoleIds: z.array(z.number()).optional(),
});

/**
 * Schema for the per-tenant default State on new clients (PD-14340). When set
 * (a US state code/name), the New Client form pre-fills the State field; empty
 * means no default. Most tenants leave it empty; it's for customers who only
 * service one state.
 */
export const ClientDefaultStateSchema = z.object({
	state: z
		.string()
		.default('')
		.describe('Default State applied to new clients (empty = no default)'),
});

// PD-14335 — tenant default credit limit. Stored as an object (the dominant
// registry convention) with a single nullable `amount`. Null = no default.
export const DefaultCreditLimitSchema = z.object({
	amount: z.number().min(0, 'Cannot be negative').nullable().default(null),
});

export type DefaultCreditLimitValue = z.infer<typeof DefaultCreditLimitSchema>;

/**
 * PD-15050 — default state of the "Email invoice to customer" checkbox shown
 * when office fulfillment creates an invoice. A checkbox rather than silent
 * automation because fulfillment is also used for corrections, where emailing
 * the customer would be wrong — the person doing it knows which case they are
 * in and can always override per invoice.
 */
export const EmailInvoiceOnFulfillmentSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Pre-tick the "Email invoice to customer" checkbox when fulfillment creates an invoice. The operator can always override it per invoice.',
		),
});

// The PD-14978 payments.daily_digest setting was retired by PD-15317: the
// recipient list on Payment Settings > Daily Digest is the digest's only
// switch (recipients configured = on), so a separate enable flag had no
// meaning. Stale instance_setting rows are simply ignored.

export type EmailInvoiceOnFulfillmentValue = z.infer<
	typeof EmailInvoiceOnFulfillmentSchema
>;

/**
 * Schema for per-entity record mastering (PD-14372). For each lockable entity
 * (client/item/vendor/license, and any future entity in LOCKABLE_ENTITIES), a
 * tenant may set which system is master and override the locked-field lists.
 * A loose `z.record(string, …)` keeps it scalable — adding a new lockable
 * entity is a registry change, not a schema change. Absent keys fall back to
 * the code registry defaults (external master + historical hardcoded fields),
 * so the default `{}` reproduces today's behavior for every tenant.
 */
/**
 * Schemas for billing modes and cycle billing (PD-15708 / PD-15846).
 *
 * `payments.billing_modes` is the master switch for the billing-mode
 * feature set (per-client billing mode, bill-with-parent, cycle billing
 * and the cycle payment schedule). It is OFF by default: a tenant that
 * has not turned it on sees no behaviour change anywhere.
 *
 * It may only be turned on for a tenant whose invoices are mastered in
 * Workd (`integrations.record_mastering` -> `invoice.master === 'workd'`).
 * Cycle billing raises and closes receivables in Workd; on a tenant whose
 * ERP owns the invoice that would create a second, conflicting source of
 * truth. The rule is enforced on write — see
 * `instance-setting-cross-validation.ts`.
 *
 * The remaining four settings only take effect while billing modes are
 * on; they tune how a cycle occurrence is planned and released.
 */
export const BillingModesSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Master switch for billing modes (per-client billing mode, bill with parent, cycle billing). Off by default. Requires invoices to be Workd-mastered.',
		),
});

export const CycleReviewRequiredSchema = z.object({
	enabled: z
		.boolean()
		.default(true)
		.describe(
			'When ON (default), every planned cycle occurrence must be released by a person before it is charged. Turn OFF to let occurrences release automatically once their lead has elapsed.',
		),
});

export const CycleApprovalThresholdSchema = z.object({
	amount: z
		.number()
		.min(0, 'Cannot be negative')
		.nullable()
		.default(null)
		.describe(
			'Charge amount above which a cycle occurrence always needs a person to release it, even when automatic release is on. Empty = no threshold.',
		),
});

export const CycleMinimumChargeSchema = z.object({
	amount: z
		.number()
		.min(0, 'Cannot be negative')
		.default(0)
		.describe(
			'Charge amount below which a cycle occurrence is skipped and its invoices roll forward to the next cycle. 0 (default) = never skip.',
		),
});

export const CycleStatementLeadDaysSchema = z.object({
	days: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Cannot be negative')
		.max(31, 'Maximum is 31 days')
		.default(0)
		.describe(
			'Days between sending the cycle statement and charging it, giving the customer time to query it. 0 (default) = charge on the cycle date.',
		),
});

/**
 * PD-15868 — whether the payment receipt email carries the receipt PDF as an
 * attachment. The receipt HTML is always in the body; the attachment is what
 * a customer files or forwards to their bookkeeper, so it is ON by default.
 */
export const ReceiptAttachPdfSchema = z.object({
	enabled: z
		.boolean()
		.default(true)
		.describe(
			'Attach a PDF copy of the receipt to the payment receipt email. On by default.',
		),
});

/**
 * PD-16038 — the explicit on/off switch for the daily payments digest.
 *
 * PD-15317 made the recipient list the only switch (nobody configured = no
 * digest). Recipients now live on the shared Notification Recipients page
 * next to lists that are never a switch, so the digest carries its own flag:
 * it sends only when this is on AND someone is configured. Defaults to on so
 * every tenant that receives the digest today keeps receiving it.
 */
export const PaymentsDigestEnabledSchema = z.object({
	enabled: z
		.boolean()
		.default(true)
		.describe(
			'Send the daily payments digest each morning to the configured recipients. On by default; the digest still needs at least one recipient.',
		),
});

export const RecordMasterEnumSchema = z.enum(['external', 'workd']);
export const EntityMasteringConfigSchema = z.object({
	master: RecordMasterEnumSchema.optional().describe(
		'external (default): external system owns the record, its fields are locked when it has an externalId. workd: Workd owns the record, it stays editable.',
	),
	lockedFields: z
		.array(z.string())
		.optional()
		.describe(
			'Override of which standard fields are locked when external is master. Absent = the entity default list.',
		),
	lockedCustomFields: z
		.array(z.number())
		.optional()
		.describe('Custom-field ids locked when external is master.'),
});
export const RecordMasteringSchema = z
	.record(z.string(), EntityMasteringConfigSchema)
	.default({});

/**
 * Schema for the `_audit` / `_request` log-table retention windows
 * (PD-14818).
 *
 * Every tenant data change writes an `_audit` row and every mutating
 * HTTP request writes a `_request` row (body + headers included); with
 * no cleanup they grow unboundedly and are the platform's largest
 * uncontrolled data-retention surface. A nightly per-tenant janitor
 * (crm-api AUDIT_RETENTION_JANITOR queue) hard-deletes rows older than
 * these windows in bounded batches. Default 90 days for both; a tenant
 * needing a longer trail (e.g. regulatory) gets this setting raised in
 * the config app — there is deliberately NO CRM-side surface for it.
 *
 * `_audit` rows keep a dangling `_fk__request__id` after their
 * `_request` row ages out — by design; the audit row is the durable
 * record, the request row is diagnostic plumbing.
 *
 * Floor of 7 days guards against a fat-fingered 0/negative purging an
 * entire trail on the next nightly run.
 */
export const AuditRetentionSchema = z.object({
	requestDays: z
		.number()
		.int('Must be a whole number')
		.min(7, 'Minimum is 7 days')
		.max(3650, 'Maximum is 3650 days (10 years)')
		.default(90)
		.describe(
			'Days to keep _request rows (full HTTP request captures) before the nightly sweep deletes them',
		),
	auditDays: z
		.number()
		.int('Must be a whole number')
		.min(7, 'Minimum is 7 days')
		.max(3650, 'Maximum is 3650 days (10 years)')
		.default(90)
		.describe(
			'Days to keep _audit rows (the data-change audit trail) before the nightly sweep deletes them',
		),
});

/**
 * Schema for sensor BLE discovery name patterns (PD-15700). The Provision
 * Sensors screen matches advertised Bluetooth names against these prefixes
 * (`<prefix>_<code>`, e.g. `MotorCityICE_A7D3E7EC`). A prefix list rather
 * than a hardcode so a brand rename never needs a deploy.
 */
export const SensorDiscoveryPatternsSchema = z.object({
	patterns: z
		.array(z.string().min(1, 'Pattern cannot be empty'))
		.default(['MotorCityICE', 'IceMover'])
		.describe(
			'Advertised-name prefixes that identify sensor units during a Bluetooth scan.',
		),
});

/**
 * Schema for sensor temperature/offline alerting (PD-15417).
 */
export const SensorAlertsSchema = z.object({
	enabled: z
		.boolean()
		.default(true)
		.describe('Master switch for sensor temperature and offline alerts.'),
	warnAboveTempF: z
		.number()
		.int('Must be a whole number')
		.nullable()
		.default(32)
		.describe(
			'Warn when a zone temperature stays ABOVE this (°F). Null disables the above-check. Per-sensor overrides win.',
		),
	warnBelowTempF: z
		.number()
		.int('Must be a whole number')
		.nullable()
		.default(null)
		.describe(
			'Warn when a zone temperature stays BELOW this (°F). Null (default) disables the below-check.',
		),
	sustainedMinutes: z
		.number()
		.int('Must be a whole number')
		.min(10)
		.default(30)
		.describe(
			'How long a temperature must stay out of spec before alerting. Devices submit roughly every 10 minutes.',
		),
	offlineAfterMinutes: z
		.number()
		.int('Must be a whole number')
		.min(30)
		.default(90)
		.describe(
			'Alert when an assigned, previously-reporting unit has been silent this long — usually a power loss.',
		),
});

/** PD-16525 — the longest a Resolved support request may wait to auto-close. */
export const PORTAL_AUTO_CLOSE_MAX_DAYS = 90;

/**
 * Schema for closing storefront support requests left at Resolved (PD-16525).
 *
 * When staff mark a storefront support request Resolved, the customer is
 * asked whether that solved it. A request still Resolved after `days` (no
 * confirmation, no reply) is moved to Done by the nightly
 * `portal-issue-auto-close` job. On by default: a request that waits forever
 * for a confirmation nobody gives would sit in the team's lists as neither
 * open nor closed.
 */
export const PortalAutoCloseSchema = z.object({
	enabled: z
		.boolean()
		.default(true)
		.describe(
			'Close storefront support requests left at Resolved after the waiting period.',
		),
	days: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Cannot be negative')
		.max(
			PORTAL_AUTO_CLOSE_MAX_DAYS,
			`Maximum is ${PORTAL_AUTO_CLOSE_MAX_DAYS} days`,
		)
		.default(7)
		.describe(
			'Days a Resolved request waits for the customer before it is closed. 0 closes it at the next nightly run.',
		),
});

/**
 * Schema for the Visible KPIs selection (PD-13518).
 *
 * `metrics` is the company-wide list; `roleRestrictions` narrows it for
 * individual roles. Both are validated against the shared goal-metric catalog
 * so a typo or a retired key can never be stored. Order is not significant —
 * every consumer re-orders by the catalog — and duplicates are harmless
 * because readers treat the lists as sets.
 */
const GoalMetricKeySchema = z.enum(
	GOAL_METRIC_NAMES as [string, ...Array<string>],
);

export const VisibleKpisSchema = z.object({
	metrics: z
		.array(GoalMetricKeySchema)
		.describe(
			'The goal-metric keys that are visible in this tenant. An empty list hides every KPI.',
		),
	roleRestrictions: z
		.array(
			z.object({
				roleId: z
					.number()
					.int('Role ids must be whole numbers')
					.describe('`_rbac_role._id` this restriction applies to.'),
				metrics: z
					.array(GoalMetricKeySchema)
					.describe(
						'The subset of the company list this role may see. Empty hides every KPI from the role.',
					),
			}),
		)
		.optional()
		.describe(
			'Per-role narrowings of the company list. A role that is absent is unrestricted and sees the whole company list; roles a user holds are unioned, so one unrestricted role means full visibility.',
		),
});

/**
 * PD-15754 — the month the tenant's fiscal year starts in. Reports offer
 * This/Last fiscal year and quarter presets and compare fiscal periods with the
 * same period a year earlier. January (the default) means the fiscal year is
 * the calendar year, and no fiscal presets are shown.
 */
export const FiscalYearSchema = z.object({
	startMonth: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Month must be 1-12')
		.max(12, 'Month must be 1-12')
		.default(1)
		.describe('First month of the fiscal year, 1 = January.'),
});

/**
 * PD-16874 — the target customer profile: what a good new customer looks
 * like, for the Sales Helper's prospect search. The company keeps one
 * (`prospecting.target_profile`, edited by admins and sales managers) and any
 * user may keep a personal copy that overrides it for their own searches
 * (`prospecting_search_profile`). An empty `businessTypes` means "no profile
 * yet": the Sales Helper then asks what makes a good customer and saves it.
 */
export const TargetCustomerProfileSchema = z.object({
	businessTypes: z
		.array(z.string().trim().min(1).max(80))
		.max(12, 'At most 12 kinds of business')
		.default([])
		.describe(
			'Kinds of businesses that make good customers, in plain words, e.g. "Chinese restaurants", "dim sum", "Asian grocers". Each is searched on Google as written.',
		),
	minReviewCount: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Cannot be negative')
		.max(100_000)
		.nullable()
		.default(null)
		// PD-16874 — Google reviews say how established or popular a place is,
		// not how big it is or how much it sells (owner, 2026-10-07), so this is
		// never described or used as a size filter.
		.describe(
			'Established or popular places only: at least this many Google reviews. Reviews show how established or popular a place is, not its size or sales. Empty = no minimum.',
		),
	independentOnly: z
		.boolean()
		.default(false)
		.describe(
			'Leave out chains: a business whose name appears at several of the places found.',
		),
	excludeNames: z
		.array(z.string().trim().min(1).max(80))
		.max(50, 'At most 50 names')
		.default([])
		.describe('Business names never to suggest, e.g. big chains.'),
	notes: z
		.string()
		.trim()
		.max(500)
		.nullable()
		.default(null)
		.describe('Anything else about a good customer, in the words of whoever set it.'),
});

/**
 * PD-12682 — the tenant's AI agent voices (owner decision 2026-10-07).
 *
 * The default voice is the tenant's English voice: agents without a voice of
 * their own speak with it, and new agents start with it selected. The
 * language list holds the voice for callers speaking each other language.
 * An agent's own per-language voices still come first; see
 * `resolveReplyVoiceId` for the full order.
 *
 * Both reference `_conversation_voice_profiles` ids, not ElevenLabs voice ids,
 * for the same reasons the agents' own `languageVoices` do: the settings
 * screen and the agent editor pick from the same voice library (with its
 * names and previews), a profile carries the voice's default speaking
 * direction, and a voice removed from the library stops being used rather
 * than lingering as an id nobody can see. The voice sync keeps every profile
 * this setting names (`ElevenlabsVoiceSyncHandler`).
 */
export const AiAgentVoicesSchema = z.object({
	defaultVoiceProfileId: z
		.number()
		.int()
		.positive()
		.nullable()
		.default(null)
		.describe('Voice profile agents use when they have no voice of their own (the English voice). Empty = Sam.'),
	languageVoices: z
		.record(
			z
				.string()
				.refine(
					(code) => code !== 'en' && PLATFORM_VOICE_LANGUAGES.has(code),
					'Must be a language the platform voice speaks, other than English (the default voice covers English)',
				),
			z.number().int().positive(),
		)
		.default({})
		.describe('Voice profile per caller language code, e.g. { "zh": 12, "yue": 13 }.'),
});

export const INSTANCE_SETTINGS_REGISTRY = {
	ACCESS_LOGGING: {
		label: 'Access Logging',
		description: 'Configure access log verbosity for debugging',
		category: 'System',
		schema: AccessLoggingSchema,
		defaultValue: { verboseLogging: false },
	},
	'branding.attribution': {
		label: 'Brand Attribution Footer',
		description:
			'Controls the "Powered by X" footer rendered across CRM web, mobile, and email PDFs. Default shows "Powered by Workd". White-label tenants can override the label or hide the footer entirely.',
		category: 'Branding',
		schema: BrandAttributionSchema,
		// `isPublic: true` allows this value to flow into the
		// pre-auth public-bootstrap response so the login screen
		// can render the attribution before a user is identified.
		// Other settings (RingCentral creds, AI throttle, etc.)
		// stay server-only by default — no `isPublic` flag, no
		// exposure.
		isPublic: true,
		defaultValue: {
			show: true,
			label: null,
			logoUrl: null,
			logoUrlDark: null,
			linkUrl: 'https://workd.com',
		},
	},
	'conversation_agents.monthly_credits': {
		label: 'AI Agent Credits',
		description: 'Monthly voice minute and LLM token limits',
		category: 'AI Agents',
		schema: ConversationAgentCreditsSchema,
		defaultValue: {
			voiceMinuteCredits: 100,
			llmTokenCredits: 1_000_000,
		},
	},
	'ai_agents.system_agent_enabled': {
		label: 'System AI Agent Enabled',
		description:
			'Enable the system AI agent (Sales Helper) chat widget for this tenant',
		category: 'AI Agents',
		schema: SystemAgentEnabledSchema,
		defaultValue: { enabled: false },
	},
	'ai_agents.voices': {
		label: 'AI Agent Voices',
		description:
			'Default voice for AI agents without their own, and the voice for each caller language. Edited in AI Agents > Admin > Voice Settings.',
		category: 'AI Agents',
		schema: AiAgentVoicesSchema,
		defaultValue: { defaultVoiceProfileId: null, languageVoices: {} },
	},
	'ringcentral.settings': {
		label: 'RingCentral Settings',
		description: 'Configuration for RingCentral click-to-dial integration',
		category: 'Integrations',
		schema: RingCentralSettingsSchema,
		defaultValue: { callMode: 'ringout' },
	},
	'throttle.concurrency': {
		label: 'AI Agent Throttle Concurrency',
		description: 'Max concurrent AI agent sessions per channel type',
		category: 'AI Agents',
		schema: ThrottleConcurrencySchema,
		defaultValue: {
			inbound: 10,
			outbound: 10,
			chat: 10,
		},
	},
	URL: {
		label: 'URLs',
		description: 'URLs related to this instance',
		category: 'System',
		schema: UrlSettingsSchema,
	},
	'pdf.invoice': {
		label: 'Invoice PDF',
		description:
			'Customize invoice PDF layout, sender info, and section visibility',
		category: 'Documents',
		schema: InvoicePdfSettingsSchema,
		defaultValue: {
			showSender: true,
			showDetailsBox: true,
			showDiscount: true,
			showShipping: true,
			remittanceInstructions: '',
		},
	},
	'printing.receipt': {
		label: 'Delivery Receipt',
		description:
			'Customize the receipt drivers print for customers on a Bluetooth thermal printer',
		category: 'Documents',
		schema: ReceiptSettingsSchema,
		defaultValue: { thankYouMessage: 'Thank you for your business!' },
	},
	'pdf.order': {
		label: 'Order PDF',
		description:
			'Customize order PDF layout, sender info, and section visibility',
		category: 'Documents',
		schema: PdfSettingsSchema,
		defaultValue: {
			showSender: true,
			showDiscount: true,
			showShipping: true,
		},
	},
	'pdf.quote': {
		label: 'Quote PDF',
		description:
			'Customize quote PDF layout, sender info, and section visibility',
		category: 'Documents',
		schema: PdfSettingsSchema,
		defaultValue: {
			showSender: true,
			showDiscount: true,
			showShipping: true,
		},
	},
	'erp.integration': {
		label: 'ERP Integration',
		description:
			"Whether Workd serves as this tenant's ERP or integrates with a third-party ERP. When Workd IS the ERP, orders remain cancellable after approval. When integrated with a third-party ERP, orders become non-cancellable once approved (because they've been pushed downstream).",
		category: 'Integrations',
		schema: ErpIntegrationSchema,
		defaultValue: { mode: 'workd_as_erp' },
	},
	'licensing.pharmacy_compliance': {
		label: 'Pharmacy License Compliance',
		description:
			'Enable pharmacy-specific compliance features including NPI, DEA verification, state board registry sync, and enhanced order blocking',
		category: 'Licensing',
		schema: PharmacyComplianceSchema,
		defaultValue: { enabled: false },
	},
	'licensing.order_license_check': {
		label: 'Order-Day License Check',
		description:
			'Hold each order in "Pending License Check" until every stamped license is re-verified good as of ship day, before it reaches the ERP. Default off; enable per tenant once matched licenses have accumulated. Requires Pharmacy License Compliance to be on.',
		category: 'Licensing',
		schema: OrderLicenseCheckSchema,
		defaultValue: { enabled: false, timeoutMinutes: 10 },
	},
	'features.pharmacy_sales': {
		label: 'Sells to Pharmacies',
		description:
			'Mark this tenant as one that sells to pharmacy clients. When ON, the CRM shows pharmacy-specific UI (NPI / NCPDP fields on the client editor, pharmacy labeling, future pharmacy-only features). Independent of the Pharmacy License Compliance setting.',
		category: 'Features',
		schema: PharmacySalesSchema,
		defaultValue: { enabled: false },
	},
	'email.domain_senders': {
		label: 'Default Sender Per Sending Domain',
		description:
			'Per-verified-domain default sender for outbound tenant email. For each verified sending domain, set the local part (the text before the @) and an optional display name. Outbound tenant email with no explicit from-address is sent from <localPart>@<primary domain>; domains with no entry keep the historical noreply. The display name is applied only when the message does not already carry one.',
		category: 'Email',
		schema: EmailDomainSendersSchema,
		defaultValue: { domains: {} },
	},
	'notifications.lead_registration_email': {
		label: 'Lead Registration Notification Email',
		description:
			'Email address (or comma-separated list) that receives a notification when a new self-serve customer lead is submitted via /api/portal/register. Per-portal_site overrides win over this tenant-level default.',
		category: 'Notifications',
		schema: LeadRegistrationEmailSchema,
		defaultValue: '',
	},
	'sensors.alerts': {
		label: 'Sensor Alerts',
		description:
			'Temperature and offline alerting for sensor-equipped merchandisers (PD-15417). Only units assigned to a customer location alert. Warn-above defaults to freezing (32°F): ice held above freezing is melting (PD-16196); per-sensor overrides win. Sustained minutes translates to consecutive ~10-minute device submissions. Offline-after covers the power-loss case: a dead unit sends no warm readings, it just goes silent. Alert recipients are configured with the document copy recipients (Sensor Alerts type).',
		category: 'Distribution',
		schema: SensorAlertsSchema,
		defaultValue: {
			enabled: true,
			warnAboveTempF: 32,
			warnBelowTempF: null,
			sustainedMinutes: 30,
			offlineAfterMinutes: 90,
		},
	},
	'sensors.discovery.namePatterns': {
		label: 'Sensor Discovery Name Patterns',
		description:
			'Advertised Bluetooth name prefixes that identify merchandiser sensor units on the Provision Sensors screen (PD-15700). Units advertise as <prefix>_<code>; only devices matching one of these prefixes appear in a scan.',
		category: 'Distribution',
		schema: SensorDiscoveryPatternsSchema,
		defaultValue: { patterns: ['MotorCityICE', 'IceMover'] },
	},
	'ordering.cross_account_delivery': {
		label: 'Allow Cross-Account Delivery',
		description:
			"When ON, CRM order entry offers 'Deliver to another customer': a salesperson can pick a different client (and one of that client's addresses) as the order's delivery destination. The order and invoice stay on the ordering client; both sides get visibility (ship-to client on the order, a Deliveries Received view on the destination client). Off by default.",
		category: 'Ordering',
		schema: CrossAccountDeliverySchema,
		defaultValue: { enabled: false },
	},
	'ordering.default_submission_status': {
		label: 'Default Order Status on Submission',
		description:
			'The status an order transitions to when submitted, set separately for CRM and ecommerce. Order-submission checks (credit hold, credit limit, etc.) still override this to Approval Required when they trigger — this only sets the default for an otherwise-clean order. Set ecommerce to Approval Required to auto-hold all storefront orders before they reach the ERP.',
		category: 'Ordering',
		schema: DefaultSubmissionStatusSchema,
		defaultValue: {
			crm: OrderStatus.PENDING,
			ecommerce: OrderStatus.PENDING,
		},
	},
	'clients.default_state': {
		label: 'Default State for New Clients',
		description:
			'When set, the New Client form pre-fills the State field with this value (still editable). Empty = no default. Useful for tenants that only service accounts in one state.',
		category: 'Clients',
		schema: ClientDefaultStateSchema,
		defaultValue: { state: '' },
	},
	'finance.default_credit_limit': {
		label: 'Default Credit Limit',
		description:
			"Default credit limit prefilled into a new client's Credit Limit on the create/edit form when it is empty. Skipped for ERP-integrated clients (those with an externalId). Empty = no default.",
		category: 'Ordering',
		schema: DefaultCreditLimitSchema,
		defaultValue: { amount: null },
	},
	'invoicing.email_invoice_on_fulfillment': {
		label: 'Email Invoice on Fulfillment',
		description:
			'Pre-sets the "Email invoice to customer" checkbox shown when office fulfillment creates an invoice, so the no-trip path can send the invoice the moment it is created. The person completing fulfillment can always untick it — fulfillment is also used for corrections, where emailing the customer would be wrong.',
		category: 'Ordering',
		schema: EmailInvoiceOnFulfillmentSchema,
		defaultValue: { enabled: false },
	},
	'ecommerce.user_role_defaults': {
		label: 'Ecommerce User Role Defaults',
		description:
			'Controls the Role field on the ecommerce user create/edit panel. Set a default role to pre-select it for new portal users, and turn OFF "allow role change" to show that role read-only and always save it — for tenants whose staff should not be choosing between Buyer and Admin. Default (no default role, change allowed) is the historical behaviour.',
		category: 'Ecommerce',
		schema: EcommerceUserRoleDefaultsSchema,
		defaultValue: { defaultRoleName: null, allowRoleChange: true },
	},
	'users.sanitize_workd_users': {
		label: 'Sanitize Workd Users in Selectors',
		description:
			"When ON (default), Workd/Developrise staff are filtered out of user-list surfaces (Drivers list, Lead Eligibility, Activity Logs, Login History) for non-Workd callers — prevents a tenant admin from accidentally picking us. Turn OFF for internal tenants like workd-sales where Workd staff are the actual operational users. Doesn't affect assigned-entity surfaces (route default driver, trip driver, client salesperson, creator/modifier) — those always show the truth.",
		category: 'Users',
		schema: SanitizeWorkdUsersSchema,
		defaultValue: { enabled: true },
	},
	'ai.mcp.enabled': {
		label: 'Claude Connector',
		description:
			'Turn this on to let people in your company connect Claude to the CRM. Each person also needs the permission to use the Claude connector. Off by default.',
		category: 'AI Agents',
		schema: McpEnabledSchema,
		defaultValue: { enabled: false },
		tenantManageAction: RbacActions.SETTINGS.INTEGRATIONS.MANAGE.action,
	},
	'ai.mcp.allow_developer_keys': {
		label: 'Claude Connector: Accept CRM and Developer-Key Tokens',
		description:
			'Allows the Claude connector to accept CRM sign-in and Developer-key tokens before connector sign-in exists. For staging and internal testing only. Off by default.',
		category: 'AI Agents',
		schema: McpAllowDeveloperKeysSchema,
		defaultValue: { enabled: false },
	},
	'ai.rate_limits': {
		label: 'Claude Connector: Rate Limits',
		description:
			'How many Claude connector tool calls one person and the whole company may make, and how many reports the company may run at the same time. A call over a limit is refused and is not charged. Workd sets these values.',
		category: 'AI Agents',
		schema: AiRateLimitsSchema,
		defaultValue: { ...DEFAULT_AI_RATE_LIMITS },
	},
	'security.idle_timeout': {
		label: 'Idle Sign-Out',
		description:
			'Signs a user out after a period of inactivity: separate minutes for the CRM and for the ecommerce storefront. Activity is real input only (mouse, keyboard, touch, scrolling), shared across open tabs; a warning with a Stay Signed In button appears a minute before the limit, and the sign-out revokes the session on the server. On a phone with Face ID or fingerprint sign-in enabled, that sign-in counts as signing back in. Empty (default) = off. Tenant admins edit it on Settings, Authentication.',
		category: 'Users',
		schema: IdleTimeoutSchema,
		defaultValue: { crmMinutes: null, storefrontMinutes: null },
		tenantManageAction: RbacActions.SETTINGS.AUTHENTICATION.MANAGE.action,
	},
	'imports.max_rows_per_run': {
		label: 'Import Max Rows Per Run',
		description:
			'Maximum data rows a single record-import run may contain (default 10,000). Workd-held governor: geocoding of imported addresses is billed to Workd, so lower this for tenants that overuse large imports instead of relying on uploader restraint.',
		category: 'Lists',
		schema: ImportMaxRowsSchema,
		defaultValue: { maxRows: 10000 },
	},
	'geocoding.google_fallback': {
		label: 'Google Geocoding Fallback',
		description:
			'Address geocoding tries the free US Census geocoder first. When ON (default), an address the Census cannot place falls back to paid Google Geocoding; when OFF the address is saved without coordinates instead.',
		category: 'Integrations',
		schema: GoogleGeocodingFallbackSchema,
		defaultValue: { enabled: true },
	},
	'sales_routes.default_visit_cadence': {
		label: 'Default Visit Cadence',
		description:
			'How often a rep is expected to visit an account that has no cadence of its own: one value for customers (monthly by default) and one for leads (none by default). Coverage rings on the maps and the need ordering in the trip planners measure against the effective cadence.',
		category: 'Clients',
		schema: DefaultVisitCadenceSchema,
		defaultValue: DefaultVisitCadenceSchema.parse({}),
		tenantManageAction: RbacActions.SETTINGS.CLIENT_DEFAULTS.MANAGE.action,
	},
	'payments.allow_account_funds_deposit': {
		label: 'Allow Account Funds Deposits',
		description:
			'When ON (default), customers may ADD money to their account funds balance — the "Apply to Customer Account" amount on the CRM Make Payment screen and "Apply to My Account" on the storefront. Turn OFF for tenants whose ERP cannot record an on-account deposit (a card processing fee has no invoice to attach to). SPENDING existing account funds is unaffected either way: Account Funds stays selectable as a payment method and still draws the balance down.',
		category: 'Payments',
		schema: AllowAccountFundsDepositSchema,
		defaultValue: { enabled: true },
		tenantManageAction: RbacActions.SETTINGS.PAYMENT_RULES.MANAGE.action,
	},
	'payments.pay_invoices_with_credit_memos': {
		label: 'Pay Invoices With Credit Memos',
		description:
			'When ON, open credit memos (RMAs) appear as credit rows on the Make a Payment screens (CRM and customer portal). Customers apply some or all of a memo’s remaining balance against the invoices they are paying and only the net difference is charged. Requires an ERP that accepts credit-memo applications on exported payments (e.g. HF Foods Workday). OFF by default — PD-15014 removed credit memos from the payable list for every other tenant.',
		category: 'Payments',
		schema: PayInvoicesWithCreditMemosSchema,
		defaultValue: { enabled: false },
		tenantManageAction: RbacActions.SETTINGS.PAYMENT_RULES.MANAGE.action,
	},
	'payments.require_full_account_funds_spend': {
		label: 'Require Full Account Funds Spend',
		description:
			'When ON, Account Funds can only be spent in full. A payment that would leave part of the balance unused is blocked on every payment screen (CRM and portal Make Payment, invoice payment, checkout), and the automatic funds-first draw on a card payment is skipped when it would be partial. Spending exactly the whole balance still works, including when it only part-pays an invoice or is combined with a card. For tenants whose ERP cannot apply part of an on-account payment (HF Foods Workday). The CRM and portal messages are editable; {balance} is replaced with the available balance. OFF by default.',
		category: 'Payments',
		schema: RequireFullAccountFundsSpendSchema,
		defaultValue: RequireFullAccountFundsSpendSchema.parse({}),
		tenantManageAction: RbacActions.SETTINGS.PAYMENT_RULES.MANAGE.action,
	},
	'payments.block_zero_total_payments': {
		label: 'Block $0 Credit-Covered Payments',
		description:
			'When ON, a payment whose total is $0 because applied credit memos cover the selected invoices is blocked on the CRM and portal Make Payment screens, with an editable message per surface. For tenants whose ERP cannot record a zero-amount payment (HF Foods Workday refuses a zero Payment_Amount). OFF by default — PD-15441 made the $0 payment a supported shape everywhere else.',
		category: 'Payments',
		schema: BlockZeroTotalPaymentsSchema,
		defaultValue: BlockZeroTotalPaymentsSchema.parse({}),
		tenantManageAction: RbacActions.SETTINGS.PAYMENT_RULES.MANAGE.action,
	},
	'sms.senders': {
		label: 'Text Message Senders',
		description:
			'Which of the tenant’s own phone numbers each kind of automated text (sign-in codes and password resets, welcome texts, delivery notifications, storefront customer notifications) is sent from. A kind with no number uses Workd’s shared number. Edited in the CRM under Settings → Communications → Text Messages, which offers only text-capable numbers on the tenant’s Twilio sub-account.',
		category: 'Integrations',
		schema: SmsSendersSchema,
		defaultValue: {
			sign_in: null,
			welcome: null,
			delivery: null,
			notification: null,
		},
	},
	'sms.staging_allow_list': {
		label: 'Staging Test Phone Numbers',
		description:
			'Extra phone numbers that receive this tenant’s automated texts (sign-in codes, welcome and password-reset texts, storefront and delivery notifications) outside production, on top of the Workd numbers every tenant may text. Stored in E.164; no row means none. Has no effect in production. Edited in the CRM under Settings → Communications → Text Messages.',
		category: 'Integrations',
		schema: SmsStagingAllowListSchema,
		defaultValue: { numbers: [] },
	},
	'softphone.twilioSubAccountSid': {
		label: 'Twilio Subaccount SID',
		description:
			'The Twilio subaccount (AC…) that owns this tenant’s softphone numbers. Set during the subaccount migration so the runtime scopes call-control, recording, coaching, and E911 REST operations to the tenant’s own subaccount (isolation). Empty/unset = the tenant still runs on the Twilio master account.',
		category: 'Integrations',
		schema: TwilioSubAccountSidSchema,
		defaultValue: { subAccountSid: '' },
	},
	'softphone.twilioFeatureEnabled': {
		label: 'Twilio Softphone Enabled',
		description:
			'Master on/off kill-switch for the tenant human Twilio softphone (PD-14243). Off by default. Only one input to the derived "on" gate — the softphone is truly on only when this is true AND a subaccount SID + voice key are provisioned AND ≥1 Kari’s Law recipient is configured. Disabling does NOT delete credentials; re-enabling restores. Written by the config-app Enable-Phone flow and the crm-api kill-switch endpoint (same key, no drift).',
		category: 'Integrations',
		schema: TwilioFeatureEnabledSchema,
		defaultValue: { enabled: false },
	},
	'audit.retention': {
		label: 'Audit Log Retention',
		description:
			'Retention windows (days) for the _request and _audit log tables, enforced by the nightly per-tenant retention sweep. Default 90 days each. Raise for tenants needing a longer audit trail (regulatory); config-app-only — no CRM surface.',
		category: 'System',
		schema: AuditRetentionSchema,
		defaultValue: { requestDays: 90, auditDays: 90 },
	},
	'web.settings': {
		label: 'Web Settings',
		description:
			'Retention of web form submissions (all, spam and duplicates, submitter IP address and browser information) and the Privacy Policy and Terms versions a terms confirmation records. A brand may override any value in its portal site settings (webSettings). Edited in Settings → Ecommerce → Web Settings through its own endpoint (settings:web:read / settings:web:manage). PD-16577.',
		category: 'Ecommerce',
		schema: WebSettingsSchema,
		defaultValue: DEFAULT_WEB_SETTINGS,
	},
	'issues.portal_auto_close': {
		label: 'Close Resolved Support Requests',
		description:
			'When ON (default), a storefront support request left at Resolved (the customer neither confirmed it solved the problem nor replied) is closed (Done) by a nightly job after the number of days set here (7 by default; 0 closes it at the next nightly run). The customer is not notified of the close. Edited in Settings → Ecommerce → Support Requests. PD-16525.',
		category: 'Ecommerce',
		schema: PortalAutoCloseSchema,
		defaultValue: { enabled: true, days: 7 },
		tenantManageAction: RbacActions.SETTINGS.SUPPORT_REQUESTS.MANAGE.action,
	},
	'payments.billing_modes': {
		label: 'Billing Modes',
		description:
			"Master switch for billing modes: a per-client billing mode (per order / per terms / per cycle), billing a child client's invoices to its parent, and cycle billing (one card or ACH transaction per cycle covering many invoices). OFF by default — a tenant with this off sees no change to client editing, order entry, dispatch, delivery charging, statements or payment schedules. Can only be turned on when Record Mastering says Workd is the master of Invoice, because cycle billing raises and closes receivables in Workd.",
		category: 'Payments',
		schema: BillingModesSchema,
		defaultValue: { enabled: false },
	},
	'payments.cycle_review_required': {
		label: 'Cycle Billing Requires Review',
		description:
			'When ON (default), a planned cycle occurrence waits for a person to release it before the card or bank account is charged. Turn OFF once a tenant trusts the cycle run to charge on its own. Only applies when Billing Modes is on.',
		category: 'Payments',
		schema: CycleReviewRequiredSchema,
		defaultValue: { enabled: true },
		tenantManageAction: RbacActions.SETTINGS.CYCLE_BILLING.MANAGE.action,
	},
	'payments.cycle_approval_threshold': {
		label: 'Cycle Billing Approval Threshold',
		description:
			'Charge amount above which a cycle occurrence always needs a person to release it, even when automatic release is turned on. Empty (default) = no threshold. Only applies when Billing Modes is on.',
		category: 'Payments',
		schema: CycleApprovalThresholdSchema,
		defaultValue: { amount: null },
		tenantManageAction: RbacActions.SETTINGS.CYCLE_BILLING.MANAGE.action,
	},
	'payments.cycle_minimum_charge': {
		label: 'Cycle Billing Minimum Charge',
		description:
			'Charge amount below which a cycle occurrence is skipped rather than charged, and its invoices roll forward into the next cycle. Avoids processing fees on trivial amounts. 0 (default) = never skip. Only applies when Billing Modes is on.',
		category: 'Payments',
		schema: CycleMinimumChargeSchema,
		defaultValue: { amount: 0 },
		tenantManageAction: RbacActions.SETTINGS.CYCLE_BILLING.MANAGE.action,
	},
	'payments.cycle_statement_lead_days': {
		label: 'Cycle Billing Statement Lead Days',
		description:
			'Days between sending a cycle statement and charging it, so the customer has time to query the statement first. 0 (default) = charge on the cycle date. Only applies when Billing Modes is on.',
		category: 'Payments',
		schema: CycleStatementLeadDaysSchema,
		defaultValue: { days: 0 },
		tenantManageAction: RbacActions.SETTINGS.CYCLE_BILLING.MANAGE.action,
	},
	'payments.receipt_attach_pdf': {
		label: 'Attach PDF To Payment Receipts',
		description:
			'When ON (default), the payment receipt email carries a PDF copy of the receipt as an attachment as well as showing it in the body of the message. Turn OFF for tenants whose customers prefer a plain email, or whose mail filters strip attachments. Applies to the cycle billing receipt and to the "Email Receipt" action on a transaction.',
		category: 'Payments',
		schema: ReceiptAttachPdfSchema,
		defaultValue: { enabled: true },
		tenantManageAction: RbacActions.SETTINGS.PAYMENT_RULES.MANAGE.action,
	},
	'payments.daily_digest_enabled': {
		label: 'Daily Payments Digest Enabled',
		description:
			'Whether the daily payments digest email is sent. ON by default. Tenant admins toggle it on Settings, Notifications, Recipients; the digest also needs at least one recipient there. Replaces the retired payments.daily_digest setting with an explicit switch (PD-16038).',
		category: 'Payments',
		schema: PaymentsDigestEnabledSchema,
		defaultValue: { enabled: true },
		tenantManageAction:
			RbacActions.SETTINGS.NOTIFICATION_RECIPIENTS.MANAGE.action,
	},
	'integrations.record_mastering': {
		label: 'Record Mastering',
		description:
			"Per-entity source of truth for externally-linked records. For each entity (Client, Item, Vendor, License) choose whether the external system is master (default — external-id records are locked read-only in the CRM) or Workd is master (records stay editable and sync out). Also overrides which standard/custom fields are locked. Developer/admin-facing — mis-setting this only removes the edit lock; it does NOT change sync direction, so set 'Workd' only for entities whose integration pushes changes OUT. Default (empty) = today's behavior for every tenant.",
		category: 'Integrations',
		schema: RecordMasteringSchema,
		defaultValue: {},
	},
	'reporting.fiscal_year': {
		label: 'Fiscal Year',
		description:
			'The month the fiscal year starts in (PD-15754). When it is not January, reports offer This/Last fiscal year and fiscal quarter date ranges, and fiscal reports compare each period with the same period a year earlier. A fiscal year is named by the calendar year it ends in. Tenant admins set it on Settings, Calendar.',
		category: 'Sales',
		schema: FiscalYearSchema,
		defaultValue: { startMonth: 1 },
		tenantManageAction: RbacActions.SETTINGS.BUSINESS_DAYS.MANAGE.action,
	},
	'goals.visible_kpis': {
		label: 'Visible KPIs',
		description:
			'The KPI metrics this tenant uses, and optional per-role narrowings of that list. A metric that is unchecked for the company disappears everywhere — the KPI Overview cards, the KPI Details report columns and CSV, the goal lists, and the goal-setting forms. A metric unchecked for one role disappears from those same surfaces for users holding that role only. Replaces the old per-user / per-team / per-company enablement (PD-13518); goal VALUES stay per user, team and company. Default (no row) is all 14 metrics visible to every role.',
		category: 'Sales',
		schema: VisibleKpisSchema,
		defaultValue: { metrics: [...GOAL_METRIC_NAMES] },
	},
	'prospecting.target_profile': {
		label: 'Target Customer Profile',
		description:
			'What a good new customer looks like, for the Sales Helper\'s prospect search ("find me good target accounts in Chicago"): the kinds of businesses to look for on Google, a minimum Google review count for established or popular places, whether to leave out chains, and names never to suggest. Users may keep a personal profile that overrides this one for their own searches. Admins and sales managers edit it on Settings, Target Customers.',
		category: 'Sales',
		schema: TargetCustomerProfileSchema,
		defaultValue: TargetCustomerProfileSchema.parse({}),
		tenantManageAction: RbacActions.SETTINGS.TARGET_PROFILE.MANAGE.action,
	},
} as const;

/**
 * Infer TypeScript types from schemas
 */
export type AccessLoggingValue = z.infer<typeof AccessLoggingSchema>;
export type SystemAgentEnabledValue = z.infer<typeof SystemAgentEnabledSchema>;
export type AiAgentVoicesValue = z.infer<typeof AiAgentVoicesSchema>;
export type ConversationAgentCreditsValue = z.infer<
	typeof ConversationAgentCreditsSchema
>;
export type ThrottleConcurrencyValue = z.infer<
	typeof ThrottleConcurrencySchema
>;
export type RingCentralSettingsValue = z.infer<
	typeof RingCentralSettingsSchema
>;
export type PdfSettingsValue = z.infer<typeof PdfSettingsSchema>;
export type InvoicePdfSettingsValue = z.infer<typeof InvoicePdfSettingsSchema>;
export type PharmacyComplianceValue = z.infer<typeof PharmacyComplianceSchema>;
export type OrderLicenseCheckValue = z.infer<typeof OrderLicenseCheckSchema>;
export type ErpIntegrationValue = z.infer<typeof ErpIntegrationSchema>;
export type PharmacySalesValue = z.infer<typeof PharmacySalesSchema>;
export type BrandAttributionValue = z.infer<typeof BrandAttributionSchema>;
export type SanitizeWorkdUsersValue = z.infer<typeof SanitizeWorkdUsersSchema>;
export type McpEnabledValue = z.infer<typeof McpEnabledSchema>;
export type McpAllowDeveloperKeysValue = z.infer<
	typeof McpAllowDeveloperKeysSchema
>;
export type AiRateLimitsValue = z.infer<typeof AiRateLimitsSchema>;
export type EcommerceUserRoleDefaultsValue = z.infer<
	typeof EcommerceUserRoleDefaultsSchema
>;
export type ImportMaxRowsValue = z.infer<typeof ImportMaxRowsSchema>;
export type IdleTimeoutValue = z.infer<typeof IdleTimeoutSchema>;
export type AllowAccountFundsDepositValue = z.infer<
	typeof AllowAccountFundsDepositSchema
>;
export type PayInvoicesWithCreditMemosValue = z.infer<
	typeof PayInvoicesWithCreditMemosSchema
>;
export type RequireFullAccountFundsSpendValue = z.infer<
	typeof RequireFullAccountFundsSpendSchema
>;
export type BlockZeroTotalPaymentsValue = z.infer<
	typeof BlockZeroTotalPaymentsSchema
>;
export type EmailDomainSendersValue = z.infer<typeof EmailDomainSendersSchema>;
/** A single domain's configured default sender. */
export type EmailDomainSenderEntry = EmailDomainSendersValue['domains'][string];
export type CrossAccountDeliveryValue = z.infer<
	typeof CrossAccountDeliverySchema
>;
export type SensorDiscoveryPatternsValue = z.infer<
	typeof SensorDiscoveryPatternsSchema
>;
export type TwilioSubAccountSidValue = z.infer<
	typeof TwilioSubAccountSidSchema
>;
export type SmsSendersValue = z.infer<typeof SmsSendersSchema>;
export type SmsStagingAllowListValue = z.infer<
	typeof SmsStagingAllowListSchema
>;
export type DefaultSubmissionStatusValue = z.infer<
	typeof DefaultSubmissionStatusSchema
>;
export type ClientDefaultStateValue = z.infer<typeof ClientDefaultStateSchema>;
export type BillingModesValue = z.infer<typeof BillingModesSchema>;
export type CycleReviewRequiredValue = z.infer<
	typeof CycleReviewRequiredSchema
>;
export type CycleApprovalThresholdValue = z.infer<
	typeof CycleApprovalThresholdSchema
>;
export type CycleMinimumChargeValue = z.infer<typeof CycleMinimumChargeSchema>;
export type CycleStatementLeadDaysValue = z.infer<
	typeof CycleStatementLeadDaysSchema
>;
export type ReceiptAttachPdfValue = z.infer<typeof ReceiptAttachPdfSchema>;
export type PaymentsDigestEnabledValue = z.infer<
	typeof PaymentsDigestEnabledSchema
>;
export type RecordMasteringValue = z.infer<typeof RecordMasteringSchema>;
export type AuditRetentionValue = z.infer<typeof AuditRetentionSchema>;
export type VisibleKpisValue = z.infer<typeof VisibleKpisSchema>;
export type FiscalYearValue = z.infer<typeof FiscalYearSchema>;
export type PortalAutoCloseValue = z.infer<typeof PortalAutoCloseSchema>;
export type TargetCustomerProfileValue = z.infer<
	typeof TargetCustomerProfileSchema
>;

/**
 * Generic type helper for type-safe setting access
 */
export type InstanceSettingValue<
	K extends keyof typeof INSTANCE_SETTINGS_REGISTRY,
> = z.infer<(typeof INSTANCE_SETTINGS_REGISTRY)[K]['schema']>;

/**
 * Helper to get all setting keys (for UI dropdown)
 */
export type InstanceSettingKey = keyof typeof INSTANCE_SETTINGS_REGISTRY;

/**
 * Setting keys that are safe to expose to unauthenticated / pre-login
 * frontends (login screen, public storefront landing). Computed from the
 * `isPublic: true` flag in the registry so adding a new public setting
 * is one line in the registry, not two places.
 *
 * Server endpoints that surface public settings (the bootstrap response
 * served to anonymous visitors) MUST filter against this list. Anything
 * not present here is server-only.
 */
export const PUBLIC_INSTANCE_SETTING_KEYS: ReadonlyArray<InstanceSettingKey> =
	Object.entries(INSTANCE_SETTINGS_REGISTRY)
		.filter(([, cfg]) => (cfg as { isPublic?: boolean }).isPublic === true)
		.map(([key]) => key as InstanceSettingKey);

/**
 * PD-16039 — the RBAC action that lets a TENANT admin change a setting from
 * the CRM Settings area, or `null` when the key is Workd-managed and only
 * editable in the config app.
 *
 * Most instance settings are deliberately Workd-only (billing-mode master
 * switch, record mastering, throttles). A key that carries
 * `tenantManageAction` is served by the generic
 * `GET/PUT /api/system/instance-settings/:key` endpoint, which checks this
 * action for the caller. Declaring it here keeps "which keys a tenant may
 * edit" and "which permission that takes" in one place, next to the schema
 * that validates the value on write.
 */
export function getTenantManageAction(key: string): string | null {
	if (!Object.prototype.hasOwnProperty.call(INSTANCE_SETTINGS_REGISTRY, key)) {
		return null;
	}
	const config = INSTANCE_SETTINGS_REGISTRY[key as InstanceSettingKey] as {
		tenantManageAction?: string;
	};
	return config.tenantManageAction ?? null;
}

/** Keys a tenant admin may edit through the CRM (see `getTenantManageAction`). */
export const TENANT_EDITABLE_INSTANCE_SETTING_KEYS: ReadonlyArray<InstanceSettingKey> =
	Object.keys(INSTANCE_SETTINGS_REGISTRY)
		.filter((key) => getTenantManageAction(key) != null)
		.map((key) => key as InstanceSettingKey);
